import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { adminAPI } from '../../services/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Save, Loader2, Mail, TestTube, AlertCircle, CheckCircle, Eye, EyeOff } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';

const EmailSettings = () => {
  const { token } = useAuth();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [settings, setSettings] = useState({
    notificationEmail: 'atifhaq65@gmail.com',
    smtpEmail: 'atifhaq65@gmail.com',
    smtpPassword: '',
    enabled: false
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getEmailSettings(token);
      if (response.success) {
        setSettings(response.data);
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to load email settings',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setSettings({ ...settings, [field]: value });
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const response = await adminAPI.updateEmailSettings(settings, token);
      if (response.success) {
        toast({
          title: 'Success!',
          description: 'Email settings saved successfully',
        });
        await fetchSettings();
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to save email settings',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    try {
      setTesting(true);
      const response = await adminAPI.testEmailSettings(token);
      if (response.success) {
        toast({
          title: 'Success!',
          description: 'Email connection test passed',
        });
      }
    } catch (error) {
      toast({
        title: 'Test Failed',
        description: error.response?.data?.detail || 'Failed to connect to email server',
        variant: 'destructive',
      });
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="animate-spin text-orange-500" size={40} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Email Settings</h2>
          <p className="text-gray-400">Configure email notifications for contact form submissions</p>
        </div>
        <Button
          onClick={handleSave}
          disabled={saving}
          className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white border-0"
        >
          {saving ? (
            <>
              <Loader2 className="mr-2 animate-spin" size={18} />
              Saving...
            </>
          ) : (
            <>
              <Save className="mr-2" size={18} />
              Save Settings
            </>
          )}
        </Button>
      </div>

      {/* Enable/Disable */}
      <div className="glass p-6 rounded-3xl border border-orange-500/20">
        <div className="flex items-center justify-between">
          <div className="space-y-1">
            <h3 className="text-lg font-semibold text-white flex items-center gap-2">
              <Mail size={20} className="text-orange-500" />
              Email Notifications
            </h3>
            <p className="text-sm text-gray-400">
              Receive email alerts when someone submits the contact form
            </p>
          </div>
          <Switch
            checked={settings.enabled}
            onCheckedChange={(checked) => handleChange('enabled', checked)}
            className="data-[state=checked]:bg-orange-500"
          />
        </div>
      </div>

      {/* Gmail Setup Instructions */}
      <div className="glass p-6 rounded-3xl border border-blue-500/20 bg-blue-500/5">
        <h3 className="text-lg font-semibold text-blue-400 mb-3 flex items-center gap-2">
          <AlertCircle size={20} />
          Gmail App Password Setup
        </h3>
        <div className="space-y-2 text-sm text-blue-300">
          <p>To use Gmail for sending notifications, you need an App Password:</p>
          <ol className="list-decimal list-inside space-y-1 ml-2">
            <li>Go to your Google Account settings</li>
            <li>Enable 2-Step Verification (if not already enabled)</li>
            <li>Go to Security → 2-Step Verification → App passwords</li>
            <li>Generate a new app password for "Mail"</li>
            <li>Copy the 16-character password and paste it below</li>
          </ol>
          <a
            href="https://myaccount.google.com/apppasswords"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block mt-2 text-blue-400 hover:text-blue-300 underline"
          >
            → Create App Password
          </a>
        </div>
      </div>

      {/* Email Configuration */}
      <div className="glass p-6 rounded-3xl border border-orange-500/20">
        <h3 className="text-xl font-bold text-white mb-6">Email Configuration</h3>
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label className="text-gray-300">Notification Email</Label>
              <Input
                type="email"
                placeholder="your-email@gmail.com"
                value={settings.notificationEmail}
                onChange={(e) => handleChange('notificationEmail', e.target.value)}
                className="bg-zinc-900 border-orange-500/20 text-white"
              />
              <p className="text-xs text-gray-500">Where to receive contact notifications</p>
            </div>

            <div className="space-y-2">
              <Label className="text-gray-300">SMTP Email (Gmail)</Label>
              <Input
                type="email"
                placeholder="your-email@gmail.com"
                value={settings.smtpEmail}
                onChange={(e) => handleChange('smtpEmail', e.target.value)}
                className="bg-zinc-900 border-orange-500/20 text-white"
              />
              <p className="text-xs text-gray-500">Gmail account to send from</p>
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-gray-300">Gmail App Password</Label>
            <div className="relative">
              <Input
                type={showPassword ? "text" : "password"}
                placeholder="Enter 16-character app password"
                value={settings.smtpPassword}
                onChange={(e) => handleChange('smtpPassword', e.target.value)}
                className="bg-zinc-900 border-orange-500/20 text-white pr-12"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-orange-500 transition-colors"
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
            <p className="text-xs text-gray-500">
              {settings.smtpPassword === '********' 
                ? 'Password is currently set and masked. Enter new password to change, or click the eye icon to view.' 
                : showPassword 
                  ? 'Password visible. Click eye icon to hide.'
                  : 'Paste the 16-character app password from Google. Click eye icon to view.'}
            </p>
          </div>

          <div className="flex items-center gap-2 pt-4 border-t border-orange-500/10">
            <div className="flex items-center gap-2 text-sm">
              <div className="w-2 h-2 rounded-full bg-gray-500"></div>
              <span className="text-gray-400">SMTP: smtp.gmail.com:587</span>
            </div>
          </div>
        </div>
      </div>

      {/* Test Connection */}
      {settings.enabled && (
        <div className="flex justify-end">
          <Button
            onClick={handleTest}
            disabled={testing || !settings.smtpPassword || settings.smtpPassword === '********'}
            variant="outline"
            className="border-orange-500/30 text-orange-500 hover:bg-orange-500/10"
          >
            {testing ? (
              <>
                <Loader2 className="mr-2 animate-spin" size={18} />
                Testing...
              </>
            ) : (
              <>
                <TestTube className="mr-2" size={18} />
                Test Connection
              </>
            )}
          </Button>
        </div>
      )}

      {/* Status */}
      {settings.enabled && (
        <div className="glass p-4 rounded-xl border border-green-500/20 bg-green-500/5">
          <div className="flex items-center gap-2 text-green-400">
            <CheckCircle size={20} />
            <p className="text-sm">
              <strong>Email notifications are enabled.</strong> You'll receive an email at{' '}
              <span className="font-mono">{settings.notificationEmail}</span> when someone contacts you.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmailSettings;
